import { supabase } from '../lib/supabase';

/**
 * Denonciations & Quittances — lecture seule (phase 2).
 *
 * Toutes les valeurs monetaires reprennent les formules deja en place ailleurs
 * dans l'app ; rien n'est reinvente ici :
 *
 *   montant revise (HT) = subcontracts.contract_value
 *                    + SUM(subcontract_change_orders.amount WHERE status = 'signed')
 *     -> identique a getSubcontractSummary() dans subcontractFinancialService.js
 *     Cette page l'affiche en TTC : le HT est multiplie par TAX_MULTIPLIER, pour
 *     etre comparable aux montants de factures qui sont deja taxes incluses.
 *
 *   Les colonnes DP sont CUMULATIVES : DP{n} = total approuve de la premiere DP
 *   jusqu'a DP{n} incluse. La colonne "Sans DP" reste isolee, avec son propre
 *   total, et n'entre pas dans le cumul.
 *
 *   montant approuve = SUM(montant taxes incluses des sub_invoices approuvees)
 *     ou "taxes incluses" reprend exactement la regle de subInvoiceService :
 *       total_amount s'il est renseigne, sinon SUM(lignes.amount) x 1.14975
 *     -> c'est la valeur affichee comme "Montant (taxes incl.)" sur la page
 *        Facturation sous-traitants.
 *
 * On raisonne sur l'APPROBATION, pas sur le versement : une facture approuvee
 * dans une DP compte des son approbation, meme si elle n'est pas encore payee.
 * Statuts retenus : approved, partially_paid, paid.
 *
 * Difference d'implementation : getSubcontractSummary interroge un contrat a la
 * fois. Cette page en affiche N, donc les memes calculs sont faits en lot pour
 * eviter N x 5 requetes. Les formules, elles, sont inchangees.
 *
 * sub_invoices porte deux colonnes historiques vers le contrat (contract_id et
 * subcontract_id). On utilise subcontract_id partout, comme
 * subcontractFinancialService.
 */

const num = (v) => parseFloat(v) || 0;

/* ─────────────────────────────────────────────────────────────────────────
 * Nature de la denonciation — DERIVEE du contrat, jamais choisie.
 *
 * Une regie ou un bon de commande de fourniture n'a pas de total arrete :
 * le fournisseur livre au fil du chantier. Exiger un montant y serait
 * impossible a satisfaire. Un contrat a forfait, lui, a une valeur connue.
 *
 * Verifie sur 140 contrats : les 15 'material_supply' ont contract_value
 * NULL a 100 %, et 9 des 11 'labor_material' aussi.
 * ───────────────────────────────────────────────────────────────────────── */
export const OUVERTE_MODELS = ['material_supply', 'labor_material'];

export const deriveTypeDenonciation = (pricingModel) =>
  OUVERTE_MODELS?.includes(pricingModel) ? 'ouverte' : 'ferme';

// Statuts reellement utilises en base pour une facture approuvee.
// Une facture compte des son approbation, meme non encore payee.
const APPROVED_STATUSES = ['approved_to_pay', 'partially_paid', 'paid'];

// Colonne synthetique regroupant les factures approuvees sans DP.
export const NO_DP_COLUMN_ID = '__no_dp__';

/**
 * Cellules "Quittance DPn" d'un contrat : pour chaque DP, ce qu'ELLE apporte
 * (approvedAdded, paidAdded) et les cumuls DP1…DPn (approvedCumul, paidCumul).
 * "Sans DP" reste ISOLE : son apport est son total, jamais cumule.
 * `approvedBy` / `paidBy` : montants par `${subId}::${dpId}`.
 *
 * Detail au survol (facultatif) : `approvedDetailsBy` / `paidDetailsBy` donnent,
 * par `${subId}::${dpId}`, les factures de l'apport ({ invoiceId, invoiceNumber,
 * amount, invoiceTotal }). Chaque cellule porte alors details.approved /
 * details.paid (CETTE DP), priorApproved / priorPaid (cumul DP1…DPn-1) et
 * priorRange ({ from, to } : numeros des DP anterieures, null s'il n'y en a pas).
 * Les champs numeriques ci-dessus, et donc dpCellState, ne changent pas.
 */
export function buildDpCells(realDpColumns, approvedBy, paidBy, subId, withNoDp, approvedDetailsBy, paidDetailsBy) {
  const cells = {};
  let approvedCumul = 0;
  let paidCumul = 0;
  const detailsOf = (dpId) => ({
    approved: approvedDetailsBy?.[`${subId}::${dpId}`] || [],
    paid: paidDetailsBy?.[`${subId}::${dpId}`] || [],
  });
  const first = (realDpColumns || [])?.[0]?.number ?? null;
  let previousNumber = null;
  for (const dp of realDpColumns || []) {
    const approvedAdded = approvedBy?.[`${subId}::${dp?.id}`] || 0;
    const paidAdded = paidBy?.[`${subId}::${dp?.id}`] || 0;
    const priorApproved = approvedCumul;
    const priorPaid = paidCumul;
    approvedCumul += approvedAdded;
    paidCumul += paidAdded;
    cells[dp?.id] = {
      approvedAdded, paidAdded, approvedCumul, paidCumul,
      details: detailsOf(dp?.id),
      priorApproved,
      priorPaid,
      priorRange: previousNumber == null ? null : { from: first, to: previousNumber },
    };
    previousNumber = dp?.number ?? previousNumber;
  }
  if (withNoDp) {
    const approvedAdded = approvedBy?.[`${subId}::${NO_DP_COLUMN_ID}`] || 0;
    const paidAdded = paidBy?.[`${subId}::${NO_DP_COLUMN_ID}`] || 0;
    cells[NO_DP_COLUMN_ID] = {
      approvedAdded, paidAdded, approvedCumul: approvedAdded, paidCumul: paidAdded,
      details: detailsOf(NO_DP_COLUMN_ID),
      priorApproved: 0,
      priorPaid: 0,
      priorRange: null,
    };
  }
  return cells;
}

// Detail au survol : ajoute `amount` a la facture `inv` dans la liste de `key`.
// Une facture n'apparait qu'une fois par cellule, meme avec plusieurs versements.
const addDetail = (byKey, key, inv, amount, invoiceTotal) => {
  const list = byKey[key] || (byKey[key] = []);
  const found = list?.find((d) => d?.invoiceId === inv?.id);
  if (found) found.amount += amount;
  else list?.push({ invoiceId: inv?.id, invoiceNumber: inv?.invoice_number || null, amount, invoiceTotal });
};

/**
 * Etat d'une cellule "Quittance DPn" (regle R1) :
 *   released   ✓ une quittance existe sur (contrat, DP)      -> cumul paye, vert
 *   outgrown   ⚠ quittance, mais le paye cumule a augmente
 *              depuis (versement apres la signature)         -> cumul paye, ambre
 *   toRelease  ⚠ du PAYE nouveau sur cette DP, sans quittance -> cumul paye, ambre
 *   planned    de l'APPROUVE nouveau, rien de paye dessus    -> cumul approuve, gris
 *   empty      ni approuve ni paye nouveau sur cette DP      -> tiret
 * `releasedAmount` : le montant de la quittance signee sur cette DP (0 sans quittance).
 */
export function dpCellState(cell, covered, releasedAmount = 0) {
  if (covered) {
    const amount = cell?.paidCumul || 0;
    return { kind: amount - (releasedAmount || 0) > 0.005 ? 'outgrown' : 'released', amount };
  }
  if ((cell?.paidAdded || 0) > 0) return { kind: 'toRelease', amount: cell?.paidCumul || 0 };
  if ((cell?.approvedAdded || 0) > 0) return { kind: 'planned', amount: cell?.approvedCumul || 0 };
  return { kind: 'empty', amount: 0 };
}

// Meme regle que subInvoiceService : total_amount s'il existe, sinon lignes x taxes.
const TAX_MULTIPLIER = 1.14975;
const invoiceTotalWithTax = (inv) => {
  if (inv?.total_amount != null) return num(inv?.total_amount);
  const linesTotal = (inv?.sub_invoice_lines || [])?.reduce(
    (sum, l) => sum + num(l?.amount),
    0
  );
  return linesTotal * TAX_MULTIPLIER;
};

/**
 * Exposition fournisseurs d'un contrat : ce qui reste a couvrir avant de
 * pouvoir payer.
 *
 * Trois etats par fournisseur denonce :
 *   covered    une quittance FINALE existe -> plus rien a attendre
 *   partial    des partielles, pas de finale -> ecart = denonce - quittance
 *   uncovered  aucune quittance -> la totalite du montant denonce
 *
 * Un montant denonce NULL compte dans le NOMBRE mais pas dans le montant :
 * on ne connait pas la somme, et l'inventer serait pire que de l'omettre.
 * Le compteur reste donc toujours l'indicateur qui fait foi.
 */
const computeSupplierExposure = (suppliers) => {
  const details = [];
  let uncoveredCount = 0;
  let partialCount = 0;
  let uncoveredAmount = 0;

  for (const d of suppliers || []) {
    const qs = d?.quittances || [];
    const quittanceTotal = qs?.reduce((sum, q) => sum + num(q?.montant), 0);
    const hasFinale = qs?.some((q) => q?.type === 'finale');
    // null preserve : "montant inconnu" n'est pas "montant nul".
    const denonce = d?.montant_denonce == null ? null : num(d?.montant_denonce);

    let status;
    if (hasFinale) {
      status = 'covered';
    } else if (qs?.length === 0) {
      status = 'uncovered';
      uncoveredCount += 1;
      if (denonce !== null) uncoveredAmount += denonce;
    } else {
      status = 'partial';
      partialCount += 1;
      // Une quittance qui depasse le montant denonce ne cree pas d'ecart
      // negatif : le reste a couvrir plancher a zero.
      if (denonce !== null) uncoveredAmount += Math.max(0, denonce - quittanceTotal);
    }

    details?.push({
      denonciation_id: d?.id,
      intervenant_name: d?.intervenant_name,
      montant_denonce: denonce,
      quittance_total: quittanceTotal,
      // Les lignes elles-memes, pas seulement leur somme : sans elles, deux
      // quittances de 10 000 $ sont indistinguables et ingerables.
      quittances: qs,
      status,
    });
  }

  return {
    supplier_denonciations_count: details?.length,
    suppliers_uncovered_count: uncoveredCount,
    suppliers_partial_count: partialCount,
    suppliers_uncovered_amount: uncoveredAmount,
    payment_blocked: uncoveredCount > 0 || partialCount > 0,
    supplier_details: details,
  };
};

/**
 * Charge tout ce qu'il faut pour la page, en lecture seule.
 * Retourne { subcontracts, denonciations, quittances, paymentRequests, dpColumns }.
 */
export async function getProjectQuittancesData(projectId) {
  const empty = {
    subcontracts: [],
    denonciations: [],
    quittances: [],
    paymentRequests: [],
    dpColumns: [],
  };
  if (!projectId) return empty;

  // ── 1. Contrats du projet + leur sous-traitant ────────────────────────────
  const { data: subsRaw, error: subsErr } = await supabase
    ?.from('subcontracts')
    ?.select(`
      id,
      contract_value,
      pricing_model,
      status,
      company,
      subcontractor:subcontractors ( id, company_name, trade, contact_person, contact_email, contact_phone ),
      project_cost:project_costs ( cost_code, description )
    `)
    ?.eq('project_id', projectId);
  if (subsErr) throw subsErr;

  const subs = subsRaw || [];
  const subIds = subs?.map((s) => s?.id)?.filter(Boolean);

  if (subIds?.length === 0) {
    const { data: denonEmpty } = await supabase
      ?.from('denonciations')
      ?.select('*')
      ?.eq('project_id', projectId);
    return { ...empty, denonciations: denonEmpty || [] };
  }

  // ── 2. Avenants signes (meme filtre que getSubcontractSummary) ────────────
  const { data: cos, error: coErr } = await supabase
    ?.from('subcontract_change_orders')
    ?.select('subcontract_id, amount')
    ?.in('subcontract_id', subIds)
    ?.eq('status', 'signed');
  if (coErr) throw coErr;

  const signedCoBySub = {};
  for (const co of cos || []) {
    const k = co?.subcontract_id;
    signedCoBySub[k] = (signedCoBySub?.[k] || 0) + num(co?.amount);
  }

  // ── 3. Factures du projet, avec leurs versements ──────────────────────────
  const { data: invoices, error: invErr } = await supabase
    ?.from('sub_invoices')
    ?.select(`
      *,
      sub_invoice_lines ( amount )
    `)
    ?.eq('project_id', projectId)
    ?.in('status', APPROVED_STATUSES);
  if (invErr) throw invErr;

  // Cumul approuve par contrat : toutes les factures approuvees comptent,
  // qu'elles soient rattachees a une DP ou non.
  // Ventilation par colonne : une entree par DP, plus une colonne "Sans DP"
  // pour les factures approuvees non rattachees. La somme des colonnes egale
  // donc toujours le cumul.
  const approvedBySub = {};
  // HT NATIF : la somme des lignes de facture. C'est exactement le nombre que
  // invoiceTotalWithTax multiplie par TAX_MULTIPLIER pour obtenir le TTC.
  // On ne divise rien : on garde la valeur d'avant la multiplication.
  const approvedHtBySub = {};
  const approvedBySubDp = {}; // `${subId}::${dpId|NO_DP_COLUMN_ID}`
  const approvedDetailsBySubDp = {}; // meme clef, factures qui composent le montant
  const dpWithApproved = new Set();
  let hasNoDpApproved = false;
  for (const inv of invoices || []) {
    const subId = inv?.subcontract_id;
    if (!subId) continue; // facture non rattachee a un contrat
    const amount = invoiceTotalWithTax(inv);
    if (amount === 0) continue;

    approvedBySub[subId] = (approvedBySub?.[subId] || 0) + amount;
    approvedHtBySub[subId] =
      (approvedHtBySub?.[subId] || 0) +
      (inv?.sub_invoice_lines || [])?.reduce((sum, l) => sum + num(l?.amount), 0);

    const dpId = inv?.payment_request_id || NO_DP_COLUMN_ID;
    if (dpId === NO_DP_COLUMN_ID) hasNoDpApproved = true;
    else dpWithApproved?.add(dpId);

    const key = `${subId}::${dpId}`;
    approvedBySubDp[key] = (approvedBySubDp?.[key] || 0) + amount;
    addDetail(approvedDetailsBySubDp, key, inv, amount, amount);
  }

  // ── 3bis. Versements REELLEMENT payes ─────────────────────────────────────
  //
  // Une quittance atteste d'un paiement, pas d'une approbation. Le montant
  // par DP vient donc de sub_payments, pas de sub_invoices.
  //
  // Requete SEPAREE, volontairement SANS filtre de statut : un versement
  // enregistre sur une facture restee 'received' serait invisible si on
  // reutilisait la requete approuvee ci-dessus.
  //
  // amount_paid est DEJA net de retenue — recordPayment relit net_payable
  // depuis sub_invoice_approvals. Ne jamais rededuire la retenue ici.
  const paidBySubDp = {}; // `${subId}::${dpId|NO_DP_COLUMN_ID}`
  const paidDetailsBySubDp = {}; // meme clef, factures qui composent le montant
  try {
    const { data: payments, error: payErr } = await supabase
      ?.from('sub_payments')
      ?.select('amount_paid, sub_invoice:sub_invoices!inner ( id, invoice_number, total_amount, subcontract_id, payment_request_id, project_id, sub_invoice_lines ( amount ) )')
      ?.eq('sub_invoice.project_id', projectId);
    if (payErr) throw payErr;

    for (const pay of payments || []) {
      const inv = pay?.sub_invoice;
      const subId = inv?.subcontract_id;
      if (!subId) continue; // versement sur une facture non rattachee a un contrat
      const amount = num(pay?.amount_paid);
      if (amount === 0) continue;
      const dpId = inv?.payment_request_id || NO_DP_COLUMN_ID;
      const key = `${subId}::${dpId}`;
      paidBySubDp[key] = (paidBySubDp?.[key] || 0) + amount;
      addDetail(paidDetailsBySubDp, key, inv, amount, invoiceTotalWithTax(inv));
    }
  } catch (err) {
    // Colonnes vides plutot que page blanche.
    console.error('[denonciationsQuittances] payments load failed:', err);
  }

  // ── 4. Demandes de paiement du projet ─────────────────────────────────────
  const { data: dps, error: dpErr } = await supabase
    ?.from('payment_requests')
    ?.select('id, number, status, period_start, period_end')
    ?.eq('project_id', projectId)
    ?.order('number', { ascending: true });
  if (dpErr) throw dpErr;

  // Une colonne par DP portant au moins une facture approuvee,
  // puis la colonne "Sans DP" en dernier si elle porte quelque chose.
  const dpColumns = (dps || [])?.filter((dp) => dpWithApproved?.has(dp?.id));
  if (hasNoDpApproved) {
    dpColumns?.push({ id: NO_DP_COLUMN_ID, number: null, status: null, isNoDp: true });
  }

  // ── 5. Denonciations du projet ────────────────────────────────────────────
  const { data: denonciations, error: denErr } = await supabase
    ?.from('denonciations')
    ?.select('*')
    ?.eq('project_id', projectId)
    ?.order('date_denonciation', { ascending: false, nullsFirst: false });
  if (denErr) throw denErr;

  // ── 5b. Documents des denonciations, en une seule requete ────────────────
  let documentsByDenonciation = {};
  try {
    documentsByDenonciation = await listDocumentsFor(
      (denonciations || [])?.map((d) => d?.id)
    );
  } catch (err) {
    // La table peut ne pas encore exister : la page reste utilisable sans.
    console.error('[denonciationsQuittances] documents load failed:', err);
  }

  // ── 6. Quittances du projet ───────────────────────────────────────────────
  // Meme precaution que pour les documents : la table peut ne pas encore
  // exister. Une erreur ici ne doit pas vider toute la page.
  let quittances = [];
  try {
    const { data: qData, error: qErr } = await supabase
      ?.from('quittances')
      ?.select('*')
      ?.eq('project_id', projectId);
    if (qErr) throw qErr;
    quittances = qData || [];
  } catch (err) {
    console.error('[denonciationsQuittances] quittances load failed:', err);
  }

  // ── 6b. Index des quittances par cible ────────────────────────────────────
  // La requete ci-dessus filtre par project_id, PAS par subcontract_id : les
  // quittances de fournisseur (subcontract_id NULL) y sont donc bien. Mais
  // elles ne sont atteignables que par denonciation_id — indexer uniquement
  // par contrat les rendrait invisibles.
  const quittancesByDenonciation = {};
  for (const q of quittances) {
    const k = q?.denonciation_id;
    if (!k) continue;
    if (!quittancesByDenonciation?.[k]) quittancesByDenonciation[k] = [];
    quittancesByDenonciation?.[k]?.push(q);
  }

  // ── 6c. Quittances appartenant a un FOURNISSEUR ──────────────────────────
  // Une quittance de fournisseur porte le subcontract_id du sous-traitant :
  // la colonne est NOT NULL, et c'est bien ce contrat qui est en jeu. Sans
  // discriminateur elle contaminerait l'affichage du sous-traitant — pastille
  // sur sa ligne, case verdie, cumul fausse.
  //
  // Le discriminateur : sa denonciation est SECONDAIRE. La page exclut ces
  // ids de tout ce qui concerne le sous-traitant.
  const secondaryDenonIds = new Set(
    (denonciations || [])?.filter((d) => d?.is_principal === false)?.map((d) => d?.id)
  );
  const supplierQuittanceIds = (quittances || [])
    ?.filter((q) => q?.denonciation_id && secondaryDenonIds?.has(q?.denonciation_id))
    ?.map((q) => q?.id);

  // Denonciations enrichies de leurs documents ET de leurs quittances. C'est
  // cette version qui est indexee par contrat, sinon l'onglet Quittances ne
  // verrait ni document ni couverture.
  const denonciationsWithDocs = (denonciations || [])?.map((d) => ({
    ...d,
    documents: documentsByDenonciation?.[d?.id] || [],
    quittances: quittancesByDenonciation?.[d?.id] || [],
  }));

  const denonBySub = {};
  // Index SEPARE des denonciations PRINCIPALES. Une quittance de
  // sous-traitant se rattache a celle-la, jamais a une secondaire : une
  // quittance de fournisseur est un autre objet. denonBySub, lui, reste
  // toutes categories confondues — il alimente rowStatus et la colonne
  // "Date denonciation", qui ne doivent pas changer.
  const denonPrincipaleBySub = {};
  for (const d of denonciationsWithDocs) {
    const k = d?.subcontract_id;
    if (!k) continue;
    // la plus recente l'emporte (deja triee desc)
    if (!denonBySub?.[k]) denonBySub[k] = d;
    if (d?.is_principal === true && !denonPrincipaleBySub?.[k]) {
      denonPrincipaleBySub[k] = d;
    }
  }

  // ── 6c. Fournisseurs denonces SOUS chaque contrat ─────────────────────────
  // C'est le coeur de la regle : ne pas payer X tant que son fournisseur Y
  // n'a pas quittance.
  //
  // La cle est subcontract_id, seul lien de contrat du modele. Sur une
  // SECONDAIRE il designe le contrat du sous-traitant que ce fournisseur
  // approvisionne — donc exactement le contrat a ne pas payer.
  // (parent_subcontract_id est abandonne : jamais alimente, jamais lu.)
  const suppliersBySubcontract = {};
  for (const d of denonciationsWithDocs) {
    if (d?.is_principal !== false) continue;
    const k = d?.subcontract_id;
    if (!k) continue;
    if (!suppliersBySubcontract?.[k]) suppliersBySubcontract[k] = [];
    suppliersBySubcontract?.[k]?.push(d);
  }

  // ── 7. Assemblage d'une ligne par contrat ─────────────────────────────────
  // Les colonnes DP reelles, dans l'ordre, servent au cumul progressif.
  const realDpColumns = dpColumns?.filter((dp) => !dp?.isNoDp);

  const rows = subs?.map((s) => {
    const isLaborMaterial = (s?.pricing_model || 'fixed') === 'labor_material';
    // Un montant de contrat INCONNU donne un montant revise INCONNU, jamais
    // zero. num() coalesce un NULL en 0 ; le laisser passer produisait une
    // balance de 0 - facture, soit un negatif qui ne veut rien dire.
    // Les bons de commande de fourniture n'ont pas de contract_value.
    //
    // == null couvre null ET undefined. isLaborMaterial reste teste a part :
    // une regie demeure inconnue meme si quelqu'un y saisit un montant.
    const revisedHt =
      isLaborMaterial || s?.contract_value == null
        ? null
        : num(s?.contract_value) + (signedCoBySub?.[s?.id] || 0);
    // Affiche en TTC pour etre comparable aux factures, deja taxes incluses.
    const revised = revisedHt === null ? null : revisedHt * TAX_MULTIPLIER;
    const denonciation = denonBySub?.[s?.id] || null;

    // Cumul progressif sur les DP reelles ; "Sans DP" garde son total propre.
    const perDp = {};
    let running = 0;
    for (const dp of realDpColumns) {
      running += approvedBySubDp?.[`${s?.id}::${dp?.id}`] || 0;
      perDp[dp?.id] = running;
    }
    const sansDp = approvedBySubDp?.[`${s?.id}::${NO_DP_COLUMN_ID}`] || 0;
    if (hasNoDpApproved) perDp[NO_DP_COLUMN_ID] = sansDp;

    // Meme cumul, sur l'argent REELLEMENT VERSE. C'est ce que les colonnes
    // "Quittance DPn" affichent : une quittance se signe contre un paiement.
    const perDpPaid = {};
    let runningPaid = 0;
    for (const dp of realDpColumns) {
      runningPaid += paidBySubDp?.[`${s?.id}::${dp?.id}`] || 0;
      perDpPaid[dp?.id] = runningPaid;
    }
    const sansDpPaid = paidBySubDp?.[`${s?.id}::${NO_DP_COLUMN_ID}`] || 0;
    // "Sans DP" reste ISOLE, jamais cumule — comme du cote approuve.
    if (hasNoDpApproved) perDpPaid[NO_DP_COLUMN_ID] = sansDpPaid;
    const paidToDate = runningPaid + sansDpPaid;
    // Par DP : apport et cumuls, approuve ET paye — ce que lisent les colonnes "Quittance DPn".
    const dpCells = buildDpCells(realDpColumns, approvedBySubDp, paidBySubDp, s?.id, hasNoDpApproved, approvedDetailsBySubDp, paidDetailsBySubDp);

    // Dernier cumul + hors DP = tout ce qui est approuve pour ce contrat.
    const approvedToDate = running + sansDp;
    const approvedToDateHt = approvedHtBySub?.[s?.id] || 0;

    return {
      id: s?.id,
      name: s?.subcontractor?.company_name || '—',
      // Raison sociale portee par le CONTRAT (subcontracts.company), distincte
      // de `name` qui vient de subcontractors.company_name. C'est elle que
      // denonciations.lie_a reflete — la migration A a fait le backfill avec.
      company: s?.company || null,
      trade: s?.subcontractor?.trade || null,
      contactPerson: s?.subcontractor?.contact_person || null,
      contactEmail: s?.subcontractor?.contact_email || null,
      contactPhone: s?.subcontractor?.contact_phone || null,
      // Un contrat n'a pas de numero propre : l'app l'identifie par son code
      // de cout, comme la page Contrats.
      costCode: s?.project_cost?.cost_code || null,
      costDescription: s?.project_cost?.description || null,
      pricingModel: s?.pricing_model || 'fixed',
      // Valeur BRUTE du contrat, NULL preserve. A ne pas confondre avec
      // revisedContract, qui passe par num() et vaut donc 0 — pas null —
      // quand contract_value est vide. Un 0 affiche mentirait.
      contractValue: s?.contract_value ?? null,
      isLaborMaterial,
      revisedContractHt: revisedHt,
      revisedContract: revised,
      approvedToDate,
      approvedToDateHt,
      balance: revised === null ? null : revised - approvedToDate,
      // Le HT n'existe QUE si les deux termes sont HT natifs. En regie
      // revisedHt est null : la balance HT l'est aussi, jamais 0.
      balanceHt: revisedHt === null ? null : revisedHt - approvedToDateHt,
      perDpPaid,
      paidToDate,
      denonciation,
      denonciationPrincipale: denonPrincipaleBySub?.[s?.id] || null,
      perDp,
      dpCells,
      // Exposition fournisseurs. Noms en snake_case, comme le reste de la
      // couche FACTS du Brain, pour que la meme lecture serve aux deux.
      ...computeSupplierExposure(suppliersBySubcontract?.[s?.id]),
    };
  });

  return {
    subcontracts: rows,
    denonciations: denonciationsWithDocs,
    quittances,
    supplierQuittanceIds,
    paymentRequests: dps || [],
    dpColumns,
  };
}

/* ─────────────────────────────────────────────────────────────────────────
 * CRUD Denonciations
 *
 * Le RLS filtre deja par organisation via project_id : aucune verification
 * d'organisation cote client. On se contente de renseigner project_id et
 * created_by, comme le fait project_notes.
 * ───────────────────────────────────────────────────────────────────────── */

// Champs acceptes en ecriture. Tout le reste est ignore, pour qu'un champ
// calcule ou un reliquat de formulaire ne parte jamais en base.
//
// UN SEUL LIEN DE CONTRAT : subcontract_id, renseigne dans les deux cas.
//   - PRINCIPALE  : le contrat du sous-traitant qui se denonce
//   - SECONDAIRE  : le contrat du sous-traitant que ce fournisseur approvisionne
// C'est le meme contrat qu'il ne faut pas payer tant que la quittance manque.
// La colonne est NOT NULL en base depuis la migration A.
//
// parent_subcontract_id est ABANDONNE : plus jamais ecrit ni lu.
const toDenonciationPayload = (form) => ({
  subcontract_id: form?.subcontractId || null,
  intervenant_name: form?.intervenantName?.trim() || null,
  specialite: form?.specialite?.trim() || null,
  is_principal: form?.isPrincipal !== false,
  // DERIVEE du contrat, jamais saisie. Valeur close : aucune entree ne peut
  // violer denonciations_type_check.
  //
  // Garde SECONDAIRE : le pricing_model decrit le contrat du SOUS-TRAITANT,
  // pas l'entente de fourniture de son fournisseur. Le laisser decider pour
  // une secondaire ferait diverger l'interface (montant exige) de la base
  // ('ouverte'). Tant que les regles du secondaire ne sont pas fixees, il
  // reste 'ferme'.
  type_denonciation:
    form?.isPrincipal === false
      ? 'ferme'
      : deriveTypeDenonciation(form?.contractPricingModel),
  lie_a: form?.lieA?.trim() || null,
  date_denonciation: form?.dateDenonciation || null,
  montant_denonce:
    form?.montantDenonce === '' || form?.montantDenonce == null
      ? null
      : num(form?.montantDenonce),
  notes: form?.notes?.trim() || null,
  // Chemin de stockage de la lettre. Absent du formulaire => on ne touche pas
  // a la colonne (undefined est ignore par Supabase sur un update).
  ...(form?.lettreUrl !== undefined ? { lettre_url: form?.lettreUrl || null } : {}),
});

export async function createDenonciation(projectId, form) {
  if (!projectId) throw new Error('projectId is required');
  const { data: { user } } = await supabase?.auth?.getUser();
  const payload = {
    ...toDenonciationPayload(form),
    project_id: projectId,
    created_by: user?.id || null,
  };
  const { data, error } = await supabase
    ?.from('denonciations')
    ?.insert(payload)
    ?.select()
    ?.single();
  if (error) throw error;
  return data;
}

export async function updateDenonciation(id, form) {
  if (!id) throw new Error('id is required');
  // project_id et created_by ne sont jamais reecrits.
  const { data, error } = await supabase
    ?.from('denonciations')
    ?.update(toDenonciationPayload(form))
    ?.eq('id', id)
    ?.select()
    ?.single();
  if (error) throw error;
  return data;
}

/**
 * Ce qui empeche de supprimer une denonciation.
 *
 * quittances.denonciation_id est en ON DELETE RESTRICT : la base refusera le
 * DELETE avec un 23503 illisible. On compte AVANT, pour afficher la meme
 * liste "Suppression impossible" que pour les contrats.
 *
 * Les documents sont comptes a titre INFORMATIF : leur FK est en CASCADE,
 * ils ne bloquent rien. Mais partir avec trois pieces jointes sans le savoir
 * n'est pas la meme decision que supprimer une fiche vide.
 */
export async function getDenonciationDeleteBlockers(denonciationId) {
  const blockers = { quittances: 0, documents: 0, total: 0 };
  if (!denonciationId) return blockers;

  const countIn = async (table) => {
    try {
      const { count, error } = await supabase
        ?.from(table)
        ?.select('id', { count: 'exact', head: true })
        ?.eq('denonciation_id', denonciationId);
      if (error) throw error;
      return count || 0;
    } catch (err) {
      // Table absente : on ne bloque pas sur ce qu'on ne peut pas compter.
      console.error(`getDenonciationDeleteBlockers: ${table} unavailable`, err?.message);
      return 0;
    }
  };

  blockers.quittances = await countIn('quittances');
  blockers.documents = await countIn('denonciation_documents');
  // Seules les quittances bloquent : le total ne compte qu'elles.
  blockers.total = blockers.quittances;
  return blockers;
}

export async function deleteDenonciation(id) {
  if (!id) throw new Error('id is required');
  const { error } = await supabase?.from('denonciations')?.delete()?.eq('id', id);
  if (error) throw error;
  return true;
}

/* ─────────────────────────────────────────────────────────────────────────
 * CRUD Quittances
 *
 * MONTANT FIGE. Le montant est calcule par l'appelant au moment de
 * l'emission, puis stocke tel quel. Il n'est JAMAIS recalcule ensuite : une
 * facture approuvee apres coup ferait mentir une quittance deja signee. Ce
 * qui est dans la colonne `montant` est ce qui a ete quittance, point.
 *
 * Comme pour les denonciations, le RLS filtre par organisation a travers
 * project_id : aucune verification d'organisation cote client.
 * ───────────────────────────────────────────────────────────────────────── */

export const QUITTANCE_TYPES = ['partielle', 'finale'];

// Meme principe que toDenonciationPayload : liste blanche stricte.
//
// LES DEUX LIENS COEXISTENT. L'ancienne garde XOR defendait
// CHECK quittances_exactly_one_target, SUPPRIME en production : une quittance
// porte desormais son contrat ET, quand elle existe, la denonciation
// PRINCIPALE de ce contrat. Conserver cette garde mettait subcontract_id a
// null des qu'une denonciation etait rattachee — ce qui viole le NOT NULL
// pose par la migration A et faisait echouer toute emission.
//
// subcontract_id reste la cible obligatoire ; denonciation_id est un lien
// supplementaire, nullable.
const toQuittancePayload = (form) => ({
  subcontract_id: form?.subcontractId || null,
  denonciation_id: form?.denonciationId || null,
  // null = quittance emise hors demande de paiement ("Sans DP").
  payment_request_id: form?.paymentRequestId || null,
  type: form?.type === 'finale' ? 'finale' : 'partielle',
  montant: num(form?.montant),
  date_quittance: form?.dateQuittance || null,
  notes: form?.notes?.trim() || null,
});

export async function listQuittances(projectId) {
  if (!projectId) return [];
  const { data, error } = await supabase
    ?.from('quittances')
    ?.select('*')
    ?.eq('project_id', projectId)
    ?.order('created_at', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function createQuittance(projectId, form) {
  if (!projectId) throw new Error('projectId is required');
  // Exactement une cible, comme en base.
  if (!form?.subcontractId && !form?.denonciationId) {
    throw new Error('subcontractId or denonciationId is required');
  }
  const { data: { user } } = await supabase?.auth?.getUser();
  const payload = {
    ...toQuittancePayload(form),
    project_id: projectId,
    created_by: user?.id || null,
  };
  const { data, error } = await supabase
    ?.from('quittances')
    ?.insert(payload)
    ?.select()
    ?.single();
  if (error) throw error;
  return data;
}

export async function deleteQuittance(id) {
  if (!id) throw new Error('id is required');
  const { error } = await supabase?.from('quittances')?.delete()?.eq('id', id);
  if (error) throw error;
  return true;
}

/* ─────────────────────────────────────────────────────────────────────────
 * Lettre de denonciation — stockage
 *
 * Bucket reutilise : subcontractor-documents. C'est le seul bucket prive dont
 * le RLS cloisonne reellement par organisation :
 *   (storage.foldername(name))[1] = current_user_org_id()::text
 * Le premier segment du chemin DOIT donc etre l'organisation, pas le projet.
 * Chemin retenu : {orgId}/denonciations/{projectId}/{denonciationId}/{fichier}
 *
 * Types acceptes par le bucket : application/pdf, image/jpeg, image/png.
 * (pas de webp : le bucket le refuserait cote serveur)
 * ───────────────────────────────────────────────────────────────────────── */

const LETTRE_BUCKET = 'subcontractor-documents';
export const LETTRE_ALLOWED_MIMES = ['application/pdf', 'image/jpeg', 'image/png'];
export const LETTRE_MAX_BYTES = 10 * 1024 * 1024; // 10 Mo

// Nom de fichier sur : on ne garde que des caracteres surs pour un chemin.
const safeFileName = (name) =>
  (name || 'lettre')
    ?.normalize('NFD')
    ?.replace(/[\u0300-\u036f]/g, '')
    ?.replace(/[^a-zA-Z0-9._-]/g, '_')
    ?.replace(/_+/g, '_')
    ?.slice(-80);

export function validateLettreFile(file) {
  if (!file) return null;
  if (!LETTRE_ALLOWED_MIMES?.includes(file?.type)) return 'INVALID_TYPE';
  if (file?.size > LETTRE_MAX_BYTES) return 'FILE_TOO_LARGE';
  return null;
}

/**
 * Televerse la lettre et retourne le chemin de stockage a mettre dans
 * denonciations.lettre_url. `upsert` remplace proprement un fichier existant.
 */
export async function uploadLettre({ orgId, projectId, denonciationId, file }) {
  const problem = validateLettreFile(file);
  if (problem) throw new Error(problem);
  if (!orgId) throw new Error('MISSING_ORG');

  const path = `${orgId}/denonciations/${projectId}/${denonciationId}/${safeFileName(file?.name)}`;
  const { error } = await supabase?.storage
    ?.from(LETTRE_BUCKET)
    ?.upload(path, file, { upsert: true, contentType: file?.type });
  if (error) throw error;
  return path;
}

/** URL signee courte duree pour ouvrir la lettre. */
export async function getLettreSignedUrl(path) {
  if (!path) return null;
  const { data, error } = await supabase?.storage
    ?.from(LETTRE_BUCKET)
    ?.createSignedUrl(path, 3600);
  if (error) {
    console.error('Error signing lettre URL:', error);
    return null;
  }
  return data?.signedUrl || null;
}

/** Supprime le fichier ; l'echec n'est pas bloquant (la ligne prime). */
export async function removeLettre(path) {
  if (!path) return;
  try {
    await supabase?.storage?.from(LETTRE_BUCKET)?.remove([path]);
  } catch (err) {
    console.error('Error removing lettre:', err);
  }
}

/* ─────────────────────────────────────────────────────────────────────────
 * Quittance signee — stockage
 *
 * Une quittance ne porte qu'UN document signe : c'est la piece qui fait foi.
 * Il vit donc dans la colonne quittances.lettre_url, pas dans une table de
 * documents. Meme bucket et meme regle de cloisonnement que les lettres de
 * denonciation, avec un prefixe distinct :
 *   {orgId}/quittances/{projectId}/{quittanceId}/{fichier}
 * ───────────────────────────────────────────────────────────────────────── */

/**
 * Televerse la quittance signee et pointe lettre_url dessus.
 * Remplacement : l'ancien fichier n'est efface qu'APRES que la ligne pointe
 * sur le nouveau. Un echec de suppression laisse un orphelin dans le bucket,
 * jamais une reference morte en base.
 */
export async function uploadQuittanceSignee({ orgId, projectId, quittanceId, file, previousPath }) {
  const problem = validateLettreFile(file);
  if (problem) throw new Error(problem);
  if (!orgId) throw new Error('MISSING_ORG');
  if (!quittanceId) throw new Error('MISSING_QUITTANCE');

  const path = `${orgId}/quittances/${projectId}/${quittanceId}/${safeFileName(file?.name)}`;
  const { error: upErr } = await supabase?.storage
    ?.from(LETTRE_BUCKET)
    ?.upload(path, file, { upsert: true, contentType: file?.type });
  if (upErr) throw upErr;

  const { data, error } = await supabase
    ?.from('quittances')
    ?.update({ lettre_url: path })
    ?.eq('id', quittanceId)
    ?.select()
    ?.single();
  if (error) throw error;

  if (previousPath && previousPath !== path) await removeLettre(previousPath);
  return data;
}

/** URL signee courte duree pour ouvrir la quittance signee. */
export async function getQuittanceSigneeUrl(path) {
  return getLettreSignedUrl(path);
}

/** Detache la quittance signee : la colonne d'abord, le fichier ensuite. */
export async function removeQuittanceSignee(quittanceId, path) {
  if (!quittanceId) throw new Error('quittanceId is required');
  const { error } = await supabase
    ?.from('quittances')
    ?.update({ lettre_url: null })
    ?.eq('id', quittanceId);
  if (error) throw error;
  await removeLettre(path);
  return true;
}

/* ─────────────────────────────────────────────────────────────────────────
 * Documents d'une denonciation (table denonciation_documents)
 *
 * Remplace lettre_url, qui reste en base mais n'est plus alimente.
 * Meme bucket et meme convention de chemin que la lettre unique de 3A.2.
 * ───────────────────────────────────────────────────────────────────────── */

export async function listDocuments(denonciationId) {
  if (!denonciationId) return [];
  const { data, error } = await supabase
    ?.from('denonciation_documents')
    ?.select('*')
    ?.eq('denonciation_id', denonciationId)
    ?.order('created_at', { ascending: true });
  if (error) throw error;
  return data || [];
}

/** Charge en lot les documents de plusieurs denonciations, pour la table. */
export async function listDocumentsFor(denonciationIds) {
  const ids = (denonciationIds || [])?.filter(Boolean);
  if (ids?.length === 0) return {};
  const { data, error } = await supabase
    ?.from('denonciation_documents')
    ?.select('*')
    ?.in('denonciation_id', ids)
    ?.order('created_at', { ascending: true });
  if (error) throw error;
  const byDenonciation = {};
  for (const doc of data || []) {
    const k = doc?.denonciation_id;
    if (!byDenonciation?.[k]) byDenonciation[k] = [];
    byDenonciation?.[k]?.push(doc);
  }
  return byDenonciation;
}

/** Televerse un fichier puis enregistre la ligne. */
export async function addDocument({ orgId, projectId, denonciationId, file }) {
  const problem = validateLettreFile(file);
  if (problem) throw new Error(problem);
  if (!orgId) throw new Error('MISSING_ORG');
  if (!denonciationId) throw new Error('MISSING_DENONCIATION');

  const path = await uploadLettre({ orgId, projectId, denonciationId, file });
  const { data: { user } } = await supabase?.auth?.getUser();

  const { data, error } = await supabase
    ?.from('denonciation_documents')
    ?.insert({
      denonciation_id: denonciationId,
      project_id: projectId,
      file_path: path,
      file_name: file?.name || null,
      mime_type: file?.type || null,
      size_bytes: file?.size ?? null,
      created_by: user?.id || null,
    })
    ?.select()
    ?.single();
  if (error) throw error;
  return data;
}

/**
 * Supprime la ligne puis le fichier. La ligne prime : si le fichier resiste,
 * on ne laisse pas une reference morte dans la liste.
 */
export async function removeDocument(id, filePath) {
  if (!id) throw new Error('id is required');
  const { error } = await supabase
    ?.from('denonciation_documents')
    ?.delete()
    ?.eq('id', id);
  if (error) throw error;
  await removeLettre(filePath);
  return true;
}

export default {
  getProjectQuittancesData,
  createDenonciation,
  updateDenonciation,
  deleteDenonciation,
  getDenonciationDeleteBlockers,
  listQuittances,
  createQuittance,
  deleteQuittance,
  uploadQuittanceSignee,
  getQuittanceSigneeUrl,
  removeQuittanceSignee,
  uploadLettre,
  getLettreSignedUrl,
  removeLettre,
  validateLettreFile,
  listDocuments,
  listDocumentsFor,
  addDocument,
  removeDocument,
};
